<template>
  <v-container class="masterDetail">
    <v-row>
      <v-col cols="12" md="6" class="pageTtl">
        <h2>入力検証</h2>
        <div class="facility">（{{ selectedFacility?.name }}）</div>
      </v-col>
      <v-col cols="12" md="6" class="text-right d-flex justify-end">
        <v-card-actions class="px-0 py-0">
          <v-btn depressed small outlined @click="saveOrder">
            順番を保存
          </v-btn>
          <v-btn depressed :disabled="!selectedUnitId" small @click="open">
            <v-icon class="white--text">mdi-plus</v-icon>
            追加
          </v-btn>
        </v-card-actions>
      </v-col>

      <v-col cols="12">
        <v-card tile>
          <v-container class="">
            <v-row>
              <v-col cols="12">
                <v-row>
                  <v-col>
                    <v-autocomplete
                      v-model="selectedUnitId"
                      :items="units"
                      outlined
                      dense
                      hide-details="auto"
                      label="区画種類または備品"
                      clearable
                      item-text="name"
                      item-value="id"
                      @change="load"
                    ></v-autocomplete>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
          <custom-data-table
            :headers="table.headers"
            :items="table.items"
            item-class="pointer"
            :use-draggable="true"
            @click:row="handleClickRow"
            @updated-items="
              (v) => {
                table.items = v;
              }
            "
          >
          </custom-data-table>
          <validation-detail
            v-model="dialog"
            :fields="fields"
            @save="save"
            @destroy="destroy"
          ></validation-detail>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts">
import Vue from 'vue';
import { Validation, Field, Unit } from '@api/models';
import {
  DeleteValidationRequest,
  DeleteValidationResponse,
  OrderValidationRequest,
  OrderValidationResponse,
} from '@api-i/routes/validation/validation';
import ValidationDetail, {
  initValidationDetailItem,
} from './ValidationDetail.vue';
import { handleApiError, handleUnknownError } from '@web/modules/error-handler';
import { loadUnit, loadUnits } from '@web/modules/master-loader';
import { mapActions } from '@web/store/snackbar';
import { systemFields } from '@api/constants';

export default Vue.extend({
  name: 'MastersValidation',

  pageOptions: {
    routeConfig: {
      path: '/masters/validation',
    },
    props: {
      title: 'マスタ設定 - 入力検証',
    },
  },

  components: {
    ValidationDetail,
  },

  data: () => {
    return {
      selectedUnitId: '',
      units: [] as Unit[],
      fields: [] as Partial<Field>[],
      table: {
        headers: [
          { text: 'メッセージ', value: 'message', cellClass: 'pointer' },
        ],
        items: [] as Validation[],
      },
      dialog: {
        show: false,
        item: {},
        newMode: false,
      },
    };
  },

  computed: {
    selectedFacility() {
      return this.$store.getters['master/selectedFacility'];
    },
  },

  async mounted() {
    await this.loadUnits();
  },

  methods: {
    async loadUnits() {
      if (!this.selectedFacility) {
        return this.snackbarRegister({
          type: 'error',
          message:
            '施設が選択されていません。前の画面に戻り施設を選択して下さい。',
        });
      }
      const facilityId = this.selectedFacility.id;
      const units = await loadUnits(this, {
        facilityId: facilityId,
      });
      if (units) {
        this.units = units;
      }
    },

    async load() {
      if (!this.selectedUnitId) {
        this.table.items = [];
        return;
      }
      const unit = await loadUnit(this.selectedUnitId, this, {
        scope: 'validations',
      });

      if (unit) {
        this.table.items = unit.validations!;
        // 入力検証はシステムが使用するプロパティを追加で指定できる
        this.fields = [...unit.fields_, ...systemFields.usually];
      }
    },

    open() {
      if (!this.selectedUnitId) {
        return;
      }
      const unitId = this.selectedUnitId;
      this.$set(this.dialog, 'show', true);
      this.$set(
        this.dialog,
        'item',
        structuredClone({ ...initValidationDetailItem, unitId }),
      );
      this.$set(this.dialog, 'newMode', true);
    },

    handleClickRow(item: Validation) {
      this.$set(this.dialog, 'show', true);
      this.$set(this.dialog, 'item', structuredClone(item));
      this.$set(this.dialog, 'newMode', false);
    },

    async save() {
      try {
        const result = await this.$api({
          path: '/validation/save',
          method: 'post',
          params: this.dialog.item,
        });
        this.dialog.show = false;

        if (result) {
          await this.load();
        }
      } catch (error) {
        if (
          !handleApiError(error, this, {
            prefix: [
              'データの保存に失敗しました。下記内容を確認してください。',
            ],
          })
        ) {
          handleUnknownError(error, this);
          throw error;
        }
      }
    },

    async destroy() {
      if (!confirm('本当に削除しますか？')) {
        return;
      }
      try {
        const result = await this.$api<
          DeleteValidationResponse,
          DeleteValidationRequest
        >({
          path: '/validation/delete',
          method: 'post',
          params: this.dialog.item as DeleteValidationRequest,
        });
        this.dialog.show = false;

        if (result) {
          await this.load();
        }
      } catch (error) {
        if (
          !handleApiError(error, this, {
            prefix: ['削除に失敗しました。下記内容を確認してください。'],
          })
        ) {
          handleUnknownError(error, this);
          throw error;
        }
      }
    },

    async saveOrder() {
      if (!this.table.items) {
        return;
      }
      try {
        const result = await this.$api<
          OrderValidationResponse,
          OrderValidationRequest
        >({
          path: '/validation/order',
          method: 'post',
          params: {
            items: this.table.items.map((item, order) => {
              item.order = order + 1;
              return item;
            }),
          },
        });

        if (result) {
          await this.load();
        }
      } catch (error) {
        if (
          !handleApiError(error, this, {
            prefix: ['順番の保存に失敗しました。下記内容を確認してください。'],
          })
        ) {
          handleUnknownError(error, this);
          throw error;
        }
      }
    },

    ...mapActions({
      snackbarRegister: 'register',
    }),
  },
});
</script>

<style lang="scss" scoped></style>
